import { Button } from '../../../components/ui/button';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { openaiApi } from '../../../shared/api/openai.api';
import { toast } from 'sonner@2.0.3';

interface GenerateCaptionsBulkButtonProps {
  existingText?: string;
  imageUrl?: string;
  accounts: Array<{ id: string; name: string }>;
  postType?: 'post' | 'reel';
  onCaptionsGenerated: (captions: Map<string, string>) => void;
}

export function GenerateCaptionsBulkButton({
  existingText,
  imageUrl,
  accounts,
  postType = 'post',
  onCaptionsGenerated,
}: GenerateCaptionsBulkButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (accounts.length === 0) {
      toast.error('Нет активных аккаунтов');
      return;
    }

    setIsGenerating(true);
    
    try {
      toast.info(`Генерируем описания для ${accounts.length} ${accounts.length === 1 ? 'аккаунта' : 'аккаунтов'}...`);
      
      const { captions } = await openaiApi.generateCaptionsBulk({
        existingText,
        imageUrl,
        accounts,
        postType,
      });

      // Конвертируем массив в Map
      const captionsMap = new Map<string, string>();
      let successCount = 0;
      let errorCount = 0;

      captions.forEach((result) => {
        if (result.caption) {
          captionsMap.set(result.accountId, result.caption);
          successCount++;
        } else {
          errorCount++;
        }
      });

      if (errorCount > 0) {
        toast.warning(`Сгенерировано ${successCount}/${accounts.length} описаний. ${errorCount} ошибок.`);
      } else {
        toast.success(`✨ Сгенерировано ${successCount} уникальных описаний!`);
      }

      onCaptionsGenerated(captionsMap);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при генерации';
      toast.error(errorMessage);
      console.error('Generate captions bulk error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Не показываем кнопку если нет выбранных аккаунтов
  if (accounts.length === 0) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleGenerate}
      disabled={isGenerating}
      className="gap-1.5"
    >
      <Sparkles className="h-4 w-4" />
      {isGenerating 
        ? `Генерация для ${accounts.length}...` 
        : `Сгенерировать для ${accounts.length}`}
    </Button>
  );
}